import React, { useState, useEffect } from "react";
import {
  useCameraCalibrationContext,
  ECameraCalibrationType,
} from "./CameraCalibrationContext";
import CommandPanel from "../../../CommandPanel/CommandPanel";
import CommandButton from "../../../CommandPanel/CommandPanelButton";
import {
  IconWhiteBalance,
  IconCameraOffset,
  IconObjectiveOffset,
  IconFilterCubeOffset,
  IconPixelSize,
} from "../../../../Icons/TopCommandPanel";
import IconFlatFieldCorrection from "../../../../Icons/Command/FlatFieldCorrection";
import ToolsApi from "../../../../api/ToolsApi";
import { useTypedSelector } from "../../../../redux/reducers/RootReducer";
import { getImagerCapabilities } from "../../../../redux/reducers/ImagerCapabilitiesReducer";
import ImagerUtil from "../../../../Utility/ImagerUtil";
import {
  StageOffsetCalibration,
  ObjectiveCalibration,
  FilterCubeCalibration,
  FlatFieldCalibration,
  ImagerCalibration,
  PolarizerOffsetsCalibration,
} from "../../../../Model/ImagerCalibration";
import CameraOffsets from "./CameraOffsets";
import FilterCubeOffset from "./FilterCubeOffset";
import FlatFieldCorrection from "./FlatFieldCorrection";
import ObjectiveOffset from "./ObjectiveOffset";
import { ELightPath } from "../../../../Model/CaptureProfile";
import CalibrationApi from "../../../../api/CalibrationApi";
import LightPathUtil from "../../../../Utility/LightPathUtil";
import CommandPanelComboBox from "../../../CommandPanel/CommandPanelComboBox";
import LabelTextBox from "../../../Controls/LabelTextBox";
import ImagerManualControl from "../../../Imager/ImagerManualControl";
import CaptureProfileUtil from "../../../../Utility/CaptureProfileUtil";
import { getModuleState } from "../../../../redux/reducers/ModuleStateReducer";
import { useAlert } from "../../../Hooks/WindowHooks";
import SwitchButton from "../../../Buttons/SwitchButton";
import ImagerApi from "../../../../api/ImagerApi";
import PolarizeOffsets, { PolarizerCalibration } from "./PolarizeOffsets";
import ModalNotificationView from "../../../Common/ModalNotification";
import { ENotificationType } from "../../../../Model";
import ModalDisabledCameraCalibration from "../../../Common/ModalDisabledCameraCalibration";
import PolarizerIcon from '../../../../Icons/TopCommandPanel/Polarizer_Icon.svg'
import { CartesianCoordinate } from "../../../../Model/Coordinate";

type Props = {
  imagerIdentifier: string;
  imagerCalibration: ImagerCalibration | undefined;
  setImagerCalibration: (imagerCalibration: ImagerCalibration) => void;
  setOnApply: (onApply: { execute: () => void; disabled: boolean }) => void;
  setOnCancel: (onCancel: { execute: () => void; disabled: boolean }) => void;
  isCalibrationUpdated: boolean;
  setIsCalibrationUpdated: (isCalibrationUpdated: boolean) => void;
  freerunCapture: boolean;
  setManualControlEnabled: (status: boolean) => void
};

export default function CameraCalibrationCommandPanel({
  imagerIdentifier,
  setOnApply,
  setOnCancel,
  imagerCalibration,
  setImagerCalibration,
  isCalibrationUpdated,
  setIsCalibrationUpdated,
  freerunCapture,
  setManualControlEnabled,
}: Props) {
  const cameraCalibrationContext = useCameraCalibrationContext();
  const cameraCalibrationType =
    cameraCalibrationContext.calibration.cameraCalibrationType;
  const setCameraCalibrationType =
    cameraCalibrationContext.calibration.setCameraCalibrationType;
  const gridSize = cameraCalibrationContext.pixelSize.gridSize;
  const setGridSize = cameraCalibrationContext.pixelSize.setGridSize;
  const numberOfSquares = cameraCalibrationContext.pixelSize.numberOfSquares;
  const setNumberOfSquares =
    cameraCalibrationContext.pixelSize.setNumberOfSquares;
  const lineLength = cameraCalibrationContext.pixelSize.lineLength;
  const pixelSize = cameraCalibrationContext.pixelSize.pixelSizeValue;
  const setPixelSize = cameraCalibrationContext.pixelSize.setPixelSizeValue;
  const pixelSizeCalibration =
    cameraCalibrationContext.calibration.pixelSizeCalibration;
  const lightPath = cameraCalibrationContext.lightpath;
  const setLightPath = cameraCalibrationContext.setLightpath;

  const alertMessage = useAlert();

  const imagerCapabilitiesCollection = useTypedSelector((state) =>
    getImagerCapabilities(state)
  );
  const imagerIdentifiersWithFilterCubes =
    ImagerUtil.getImagerIdentifiersWithFilterCubes(
      imagerCapabilitiesCollection
    );
  const imagerIdentifiersWithObjectiveZoom =
    ImagerUtil.getImagerIdentifiersWithObjective(imagerCapabilitiesCollection);
  const imagerIdentifiersFlatFieldCorrection =
    ImagerUtil.getImagerIdentifiersWithFlatFieldCorrection(
      imagerCapabilitiesCollection
    );
  const totalLightpath = ImagerUtil.getTotalLightpathCount(
    imagerCapabilitiesCollection
  );
  const moduleState = useTypedSelector((state) =>
    getModuleState(state, imagerIdentifier)
  );
  const imagerState = moduleState.imager;
  if (imagerState == null) {
    throw new Error("Imager state is null");
  }

  const showCameraOffset = totalLightpath > 1;
  const showFilterCubeOffset = imagerIdentifiersWithFilterCubes.length > 0;
  const showObjectiveZoom = imagerIdentifiersWithObjectiveZoom.length > 0;
  const showFlatField = imagerIdentifiersFlatFieldCorrection.length > 0;

  const lightPathsSupportFF = ImagerUtil.getMultipleLightPathWithFlatField(
    imagerCapabilitiesCollection.byId[imagerIdentifier]
  );

  const lightPathsSupportObjectiveZoom =
    ImagerUtil.getMultipleLightPathWithObjective(
      imagerCapabilitiesCollection.byId[imagerIdentifier]
    );

  const isLightPathSupportObjective =
    ImagerUtil.getActiveLightPathSupportObjectiveZoom(
      imagerCapabilitiesCollection.byId[imagerIdentifier],
      imagerState.activeLightPath
    );

  const isLightPathSupportFFCorrection =
    ImagerUtil.getActiveLightPathSupportObjectiveZoom(
      imagerCapabilitiesCollection.byId[imagerIdentifier],
      imagerState.activeLightPath
    );

  const lightPathWithWhiteBalance = ImagerUtil.getLightPathWithWhiteBalance(
    imagerCapabilitiesCollection.byId[imagerIdentifier]
  );

  const [stageOffset, setStageOffset] = useState(
    null as StageOffsetCalibration | null
  );
  const [objectiveOffset, setObjectiveOffset] = useState(
    null as ObjectiveCalibration[] | null
  );
  const [filterCubeOffset, setFilterCubeOffset] = useState(
    null as FilterCubeCalibration[] | null
  );
  const [flatFieldCalibration, setFlatFieldCalibration] = useState(
    null as FlatFieldCalibration | null
  );
  const [polarizerOffsetsCalibration, setPolarizerOffsetsCalibration] = useState(null as PolarizerOffsetsCalibration | null);
  const [polarizerCalibrationData, setPolarizerCalibrationData] = useState<PolarizerCalibration>({
    off: {
      id: '1',
      name: 'Off',
      x: 0,
      y: 0,
      z: 0,
      selected: true
    },
    on: {
      id: '2',
      name: 'On',
      x: 0,
      y: 0,
      z: 0,
      selected: false
    }
  });

  const [showNotifModal, setShowNotifModal] = useState(false)
  const [contentNotifModal, setContentNotifModal] = useState({ body: '', onClickOK: () => { } })

  const CurrentZoom = CaptureProfileUtil.getTotalZoom(
    imagerState.captureProfile,
    lightPath
  );
  const CurrentBinning = CaptureProfileUtil.getBinning(
    imagerState.captureProfile,
    lightPath
  );

  const updateImagerCalibration = () => {
    CalibrationApi.getImagerCalibration(imagerIdentifier).then(
      (imagerCalibration) => {
        setImagerCalibration(imagerCalibration);
        setStageOffset(imagerCalibration?.stageOffset ?? null);
      }
    );
  };

  const onChangeCameraCalibrationView = (
    cameraCalibrationType: ECameraCalibrationType
  ) => {
    switch (cameraCalibrationType) {
      case ECameraCalibrationType.ObjectiveOffset: {
        const objectiveCalibration = ImagerUtil.getObjectiveZoomCalibration(
          imagerCalibration,
          imagerState.activeLightPath
        );
        setObjectiveOffset(
          objectiveCalibration ? objectiveCalibration.objectives : null
        );
        setLightPath(imagerState.activeLightPath);
        break;
      }
      case ECameraCalibrationType.FilterCubeOffset: {
        const fcc =
          imagerCalibration?.fluorescence?.filterCubes?.filterCubes ?? null;
        setFilterCubeOffset(fcc);
        setLightPath(ELightPath.Fluorescence);
        break;
      }
      case ECameraCalibrationType.FlatFieldCorrection: {
        setFlatFieldCalibration(
          ImagerUtil.getFlatFieldCalibration(
            imagerCalibration,
            imagerState.activeLightPath
          )
        );
        setLightPath(imagerState.activeLightPath);
        break;
      }
      case ECameraCalibrationType.PolarizeOffset: {
        const polarizerOffsetsCalibration = ImagerUtil.getPolarizerOffsets(
          imagerCalibration,
          imagerState.activeLightPath
        );
        setPolarizerOffsetsCalibration(polarizerOffsetsCalibration);
        setLightPath(imagerState.activeLightPath);
        break;
      }
    }

    setCameraCalibrationType(cameraCalibrationType);
  };

  useEffect(() => {
    switch (cameraCalibrationType) {
      case ECameraCalibrationType.ObjectiveOffset: {
        if (isLightPathSupportObjective) {
          const objectiveCalibration = ImagerUtil.getObjectiveZoomCalibration(
            imagerCalibration,
            imagerState.activeLightPath
          );
          setObjectiveOffset(
            objectiveCalibration ? objectiveCalibration.objectives : null
          );
        }

        setLightPath(imagerState.activeLightPath);
        break;
      }
      case ECameraCalibrationType.FilterCubeOffset: {
        const fcc =
          imagerCalibration?.fluorescence?.filterCubes?.filterCubes ?? null;
        setFilterCubeOffset(fcc);
        setLightPath(ELightPath.Fluorescence);
        break;
      }
      case ECameraCalibrationType.FlatFieldCorrection: {
        if (isLightPathSupportFFCorrection) {
          setFlatFieldCalibration(
            ImagerUtil.getFlatFieldCalibration(
              imagerCalibration,
              imagerState.activeLightPath
            )
          );
        }

        setLightPath(imagerState.activeLightPath);
        break;
      }
    }
  }, [
    imagerCalibration,
    isLightPathSupportObjective,
    cameraCalibrationType,
    isLightPathSupportFFCorrection,
  ]);

  useEffect(() => {
    setManualControlEnabled(!isCalibrationUpdated)
    setOnApply({ execute: onApply, disabled: !isCalibrationUpdated });
    setOnCancel({ execute: onCancel, disabled: !isCalibrationUpdated });
    return () => {
      setOnApply({ execute: () => { }, disabled: true });
      setOnCancel({ execute: () => { }, disabled: true });
    };
  }, [
    // stageOffset need to be added as the useEffect()'s deps argument to allow onApply() to use the latest react state
    // ref: https://css-tricks.com/dealing-with-stale-props-and-states-in-reacts-functional-components/
    // ref: https://reactjs.org/docs/hooks-faq.html#why-am-i-seeing-stale-props-or-state-inside-my-function
    stageOffset,
    isCalibrationUpdated,
    objectiveOffset,
    filterCubeOffset,
    flatFieldCalibration,
    polarizerOffsetsCalibration,
    numberOfSquares,
    gridSize,
    lineLength,
  ]);

  useEffect(() => {
    CalibrationApi.getImagerCalibration(imagerIdentifier).then(
      (imagerCalibration) => {
        setImagerCalibration(imagerCalibration);
        setStageOffset(imagerCalibration?.stageOffset ?? null);
      }
    );
  }, [imagerIdentifier]);

  const onApply = () => {
    switch (cameraCalibrationType) {
      case ECameraCalibrationType.CameraOffset: {
        if (stageOffset) {
          CalibrationApi.updateImagerCalibration(
            {
              identifier: imagerIdentifier,
              stageOffset: stageOffset,
            },
            imagerIdentifier
          ).then(() => updateImagerCalibration());
        }
        break;
      }
      case ECameraCalibrationType.ObjectiveOffset: {
        CalibrationApi.updateImagerCalibration(
          {
            identifier: imagerIdentifier,
            [LightPathUtil.toField(lightPath)]: {
              objectiveZoom: {
                objectives: objectiveOffset,
              },
            },
          },
          imagerIdentifier
        ).then(() => updateImagerCalibration());
        break;
      }
      case ECameraCalibrationType.FilterCubeOffset: {
        CalibrationApi.updateImagerCalibration(
          {
            identifier: imagerIdentifier,
            [LightPathUtil.toField(lightPath)]: {
              filterCubes: {
                filterCubes: filterCubeOffset,
              },
            },
          },
          imagerIdentifier
        ).then(() => updateImagerCalibration());
        break;
      }
      case ECameraCalibrationType.FlatFieldCorrection: {
        CalibrationApi.updateImagerCalibration(
          {
            identifier: imagerIdentifier,
            [LightPathUtil.toField(lightPath)]: {
              flatField: flatFieldCalibration,
            },
          },
          imagerIdentifier
        ).then(() => updateImagerCalibration());
        break;
      }
      case ECameraCalibrationType.PolarizeOffset: {
        CalibrationApi.updateImagerCalibration(
          {
            identifier: imagerIdentifier,
            [LightPathUtil.toField(lightPath)]: {
              polarizer: ImagerUtil.toPolarizerOffsetCalibration(polarizerCalibrationData),
            },
          },
          imagerIdentifier
        ).then(() => updateImagerCalibration());
        break;
      }
      case ECameraCalibrationType.PixelSize: {
        const calibration = {
          identifier: imagerIdentifier,
          [LightPathUtil.toField(lightPath)]: {
            pixelSize: {
              lengthInMicron: gridSize * numberOfSquares,
              lengthInPixel: lineLength,
            },
          },
        };
        CalibrationApi.updateImagerCalibration(calibration, imagerIdentifier);
      }
    }
    setIsCalibrationUpdated(false);
  };

  const onCancel = () => {
    setIsCalibrationUpdated(false);
    switch (cameraCalibrationType) {
      case ECameraCalibrationType.CameraOffset: {
        setStageOffset(imagerCalibration?.stageOffset ?? null);
        break;
      }
      case ECameraCalibrationType.ObjectiveOffset: {
        let objectiveCalibration = ImagerUtil.getObjectiveZoomCalibration(
          imagerCalibration,
          lightPath
        );
        setObjectiveOffset(
          objectiveCalibration ? objectiveCalibration.objectives : null
        );
        break;
      }
      case ECameraCalibrationType.FilterCubeOffset: {
        setFilterCubeOffset(
          imagerCalibration?.fluorescence?.filterCubes?.filterCubes ?? null
        );
        break;
      }
      case ECameraCalibrationType.FlatFieldCorrection: {
        setFlatFieldCalibration(
          ImagerUtil.getFlatFieldCalibration(imagerCalibration, lightPath)
        );
        break;
      }
      case ECameraCalibrationType.PixelSize: {
        setPixelSize(
          pixelSizeCalibration
            ? pixelSizeCalibration.lengthInMicron /
            pixelSizeCalibration.lengthInPixel
            : 1
        );
        break;
      }
      case ECameraCalibrationType.PolarizeOffset:
        setPolarizerCalibrationData(
          {
            off: {
              id: '1',
              name: 'Off',
              x: 0,
              y: 0,
              z: 0,
              selected: true
            },
            on: {
              id: '2',
              name: 'On',
              x: 0,
              y: 0,
              z: 0,
              selected: false
            }
          });
        break;
    }
  };

  const onChangeDrawPixel = () => {
    if (freerunCapture) {
      ImagerApi.stopStreaming(imagerIdentifier);
    } else {
      ImagerApi.startStreaming(imagerIdentifier);
    }
  }

  return (
    <>
      {cameraCalibrationType ===
        ECameraCalibrationType.CameraCalibrationSelection && (
          <CommandPanel
            style={{
              WebkitBoxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
              MozBoxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
              boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
            }}
          >
            <CommandPanel.TitleHeader title={"Camera Calibration"} />
            <CommandPanel.HorizontalContent>
              {lightPathWithWhiteBalance !== ELightPath.Disabled && (
                <CommandButton
                  id={ECameraCalibrationType.WhiteBalance.toString()}
                  icon={<IconWhiteBalance />}
                  content={"Calibrate White Balance"}
                  onClick={(id) => ToolsApi.whiteBalance()}
                />
              )}
              {showCameraOffset && (
                <CommandButton
                  id={ECameraCalibrationType.CameraOffset.toString()}
                  icon={<IconCameraOffset />}
                  content={"Camera Offset"}
                  onClick={(id) =>
                    onChangeCameraCalibrationView(Number.parseInt(id))
                  }
                />
              )}
              {showObjectiveZoom && (
                <CommandButton
                  id={ECameraCalibrationType.ObjectiveOffset.toString()}
                  icon={<IconObjectiveOffset />}
                  content={"Objective Offset"}
                  onClick={(id) => {
                    if (!isLightPathSupportObjective) {
                      alertMessage.showAlert(
                        `Objective Offset only supported by ${lightPathsSupportObjectiveZoom
                          .map((lp) => ELightPath[lp].toString())
                          .join(", ")} Light Path`
                      );
                      return;
                    }
                    onChangeCameraCalibrationView(Number.parseInt(id));
                  }}
                />
              )}
              <CommandButton
                id={ECameraCalibrationType.PolarizeOffset.toString()}
                icon={<img src={PolarizerIcon} alt="Polarizer-offsets" />}
                content={"Polarizer Offsets"}
                onClick={(id) => {
                  if (imagerState.activeLightPath !== ELightPath.Visible) {
                    setContentNotifModal({
                      body: `Polarizer Offset only supported by Visible Light Path`,
                      onClickOK: () => setShowNotifModal(false)
                    })
                    setShowNotifModal(true)
                    return;
                  }
                  onChangeCameraCalibrationView(Number.parseInt(id));
                }}
              />
              <ModalDisabledCameraCalibration
                bodyText={contentNotifModal.body}
                headerText={window.location.hostname}
                onClickOk={contentNotifModal.onClickOK}
                show={showNotifModal}
              />
              {showFilterCubeOffset && (
                <CommandButton
                  id={ECameraCalibrationType.FilterCubeOffset.toString()}
                  icon={<IconFilterCubeOffset />}
                  content={"Filter Cube Offset"}
                  onClick={(id) =>
                    onChangeCameraCalibrationView(Number.parseInt(id))
                  }
                />
              )}
              <CommandButton
                id={ECameraCalibrationType.PixelSize.toString()}
                icon={<IconPixelSize />}
                content={"Pixel Size"}
                onClick={(id) =>
                  onChangeCameraCalibrationView(Number.parseInt(id))
                }
              />
              {showFlatField && (
                <CommandButton
                  id={ECameraCalibrationType.FlatFieldCorrection.toString()}
                  icon={<IconFlatFieldCorrection />}
                  content={"Flat Field Correction"}
                  onClick={(id) => {
                    if (!isLightPathSupportFFCorrection) {
                      alertMessage.showAlert(
                        `Flat Field Correction only supported by ${lightPathsSupportFF
                          .map((lp) => ELightPath[lp].toString())
                          .join(", ")} Light Path`
                      );
                      return;
                    }
                    onChangeCameraCalibrationView(Number.parseInt(id));
                  }}
                />
              )}
            </CommandPanel.HorizontalContent>
          </CommandPanel>
        )}

      {cameraCalibrationType === ECameraCalibrationType.CameraOffset &&
        stageOffset && (
          <CameraOffsets
            backBtn={() => {
              onCancel();
              onChangeCameraCalibrationView(
                ECameraCalibrationType.CameraCalibrationSelection
              );
            }}
            imagerIdentifier={imagerIdentifier}
            stageOffset={stageOffset}
            setStageOffset={(updatedStageOffset) => {
              setStageOffset(updatedStageOffset);
              setIsCalibrationUpdated(true);
            }}
          />
        )}

      {cameraCalibrationType === ECameraCalibrationType.PolarizeOffset &&
        polarizerOffsetsCalibration && (
          <PolarizeOffsets
            backBtn={() => {
              onCancel();
              onChangeCameraCalibrationView(
                ECameraCalibrationType.CameraCalibrationSelection
              );
            }}
            imagerIdentifier={imagerIdentifier}
            polarizerCalibration={polarizerCalibrationData}
            setPolarizerCalibration={(updatedPolarizerCalibration) => {
              setPolarizerCalibrationData(updatedPolarizerCalibration);
              const isNotZero = ImagerUtil.isPolarizerCalibrationNotZero(updatedPolarizerCalibration);
              setIsCalibrationUpdated(isNotZero);
              if(isNotZero){
                setPolarizerOffsetsCalibration(ImagerUtil.toPolarizerOffsetCalibration(updatedPolarizerCalibration));
              }
            }}
            polarizerOffsetsCalibration={polarizerOffsetsCalibration}
          />
        )}
      {cameraCalibrationType === ECameraCalibrationType.ObjectiveOffset &&
        objectiveOffset && (
          <ObjectiveOffset
            disabled={!isLightPathSupportObjective}
            backBtn={() => {
              onCancel();
              onChangeCameraCalibrationView(
                ECameraCalibrationType.CameraCalibrationSelection
              );
            }}
            lightPath={lightPath}
            imagerIdentifier={imagerIdentifier}
            objectiveOffset={objectiveOffset}
            setObjectiveOffset={(updatedObjectiveOffset) => {
              setObjectiveOffset(updatedObjectiveOffset);
              setIsCalibrationUpdated(true);
            }}
            imagerIdentifiersWithObjectiveZoom={
              imagerIdentifiersWithObjectiveZoom
            }
          />
        )}

      {cameraCalibrationType === ECameraCalibrationType.FilterCubeOffset &&
        filterCubeOffset && (
          <FilterCubeOffset
            backBtn={() => {
              onCancel();
              onChangeCameraCalibrationView(
                ECameraCalibrationType.CameraCalibrationSelection
              );
            }}
            lightPath={lightPath}
            imagerIdentifier={imagerIdentifier}
            filterCubeOffset={filterCubeOffset}
            setFilterCubeOffset={(updatedFilterCubeOffset) => {
              setFilterCubeOffset(updatedFilterCubeOffset);
              setIsCalibrationUpdated(true);
            }}
            imagerIdentifiersWithFilterCubes={imagerIdentifiersWithFilterCubes}
          />
        )}

      {cameraCalibrationType === ECameraCalibrationType.FlatFieldCorrection &&
        flatFieldCalibration && (
          <FlatFieldCorrection
            disabled={!isLightPathSupportFFCorrection}
            backBtn={() => {
              onCancel();
              onChangeCameraCalibrationView(
                ECameraCalibrationType.CameraCalibrationSelection
              );
            }}
            imagerIdentifier={imagerIdentifier}
            lightPath={lightPath}
            flatFieldCalibration={flatFieldCalibration}
            setFlatFieldCalibration={(updatedFlatFieldCalibration) => {
              setFlatFieldCalibration(updatedFlatFieldCalibration);
              setIsCalibrationUpdated(true);
            }}
          ></FlatFieldCorrection>
        )}
      {cameraCalibrationType === ECameraCalibrationType.PixelSize && (
        <CommandPanel
          style={{
            overflow: "auto",
          }}
        >
          <CommandPanel.DarkBackArrowHeader
            title="Pixel Size Calibration"
            onBackArrowClick={() => {
              onCancel();
              onChangeCameraCalibrationView(
                ECameraCalibrationType.CameraCalibrationSelection
              );
            }}
          />
          <CommandPanelComboBox<number>
            disabled={!freerunCapture}
            label="Lightpath"
            value={lightPath}
            onChange={setLightPath}
            displayValues={ImagerUtil.getLightPathNumberOptions(
              imagerCapabilitiesCollection.byId[imagerIdentifier]
            )}
          />
          <div
            style={{
              margin: "10px",
              border: "1px solid #E0E0E0",
              borderRadius: "10px",
              padding: "10px",
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}
          >
            <div
              style={{
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingRight: "8px"
              }}
            >
              <div> Draw Pixel</div>
              <SwitchButton isOn={!freerunCapture} onToggle={onChangeDrawPixel} />
            </div>
            <LabelTextBox
              name="Grid Size"
              label="Grid Size (μm)"
              value={gridSize}
              unit=""
              onChange={(value) => {
                setGridSize(value);
                setPixelSize(
                  (value * numberOfSquares) /
                  ((lineLength * CurrentBinning) / CurrentZoom)
                );
              }}
              isInteger={true}
              min={1}
              max={1000}
              textBoxStyle={{
                flex: 1,
              }}
            />
            <LabelTextBox
              name="Number Of Squares"
              label="# of Squares"
              value={numberOfSquares}
              onChange={(value) => {
                setNumberOfSquares(value);
                setPixelSize(
                  (gridSize * value) /
                  ((lineLength * CurrentBinning) / CurrentZoom)
                );
              }}
              isInteger={true}
              min={1}
              max={1000}
              textBoxStyle={{
                flex: 1,
              }}
            />
            <LabelTextBox
              name="Line Length"
              label="Line Length"
              value={lineLength}
              isInteger={true}
              min={0}
              max={10000}
              readOnly={true}
              textBoxStyle={{
                flex: 1,
              }}
              disabled
            />
            <LabelTextBox
              name="Pixel Size"
              label="Pixel Size (μm)"
              value={pixelSize}
              isInteger={false}
              min={0}
              max={10000}
              readOnly={true}
              textBoxStyle={{
                flex: 1,
              }}
              disabled
            />
          </div>
          <ImagerManualControl
            wellLimitEnabled={false}
            imagerIdentifier={imagerIdentifier}
            style={{
              overflow: "none",
            }}
          />
        </CommandPanel>
      )}
    </>
  );
}
